/**
 * CSS-строка для поля ввода (композера) — STEP8 эт.3.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ CSS, а не inline-объекты: :hover и :focus-visible
 * inline-стилем не выразить в принципе, а без наведения кнопки выглядят
 * мёртвыми. Правило этапа 3: «hover — через CSS-файл, inline-стилем
 * наведение не выражается».
 *
 * Цвета и размеры — только из theme.ts, литеральных hex здесь быть не
 * должно (правило этапа 1).
 */
import { color, font, radius } from "./theme";

export const composerCss = `
/* КНОПКА композера: единая линейка. Плюс, эмодзи, отправка — одинаковый
   размер, одинаковые отступы, одинаковое наведение. */
.alg-composer-btn {
  font: inherit;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: none;
  border-radius: ${radius.circle};
  background: transparent;
  color: ${color.textDim};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 120ms ease-out, color 120ms ease-out;
}
.alg-composer-btn:hover:not(:disabled) {
  background: ${color.hover};
  color: ${color.text};
}
.alg-composer-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
/* Кнопка отправки — акцентная, круглая, в той же линейке. */
.alg-composer-btn.send {
  background: ${color.accent};
  color: ${color.textOnAccent};
}
.alg-composer-btn.send:hover:not(:disabled) {
  background: ${color.accentHover};
  color: ${color.textOnAccent};
}
.alg-composer-btn.send:disabled {
  opacity: 0.5;
}

/* ЕДИНАЯ УЗКАЯ СТРОКА (постановка Антона 19:11: «нормальной узкой
   красивой строкой, как в Телеграме»).
   Рамка и фон — У КОНТЕЙНЕРА, а не у textarea. Пока рамка была на поле,
   а кнопки стояли снаружи блоками, низ чата читался как три отдельных
   элемента в ряд и был высоким — «оформлено отвратительно».
   Теперь это одна пилюля: плюс слева внутри, эмодзи и отправка справа
   внутри, текст между ними. */
.alg-composer-bar {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding: 3px;
  border: 1px solid ${color.border};
  border-radius: ${radius.pill}px;
  background: ${color.bg};
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
/* Фокус показываем НА СТРОКЕ: внутри неё поле без своей рамки, и без
   этого набор текста никак не отражался бы визуально. */
.alg-composer-bar:focus-within {
  border-color: ${color.accent};
  box-shadow: 0 0 0 3px ${color.accentSoft};
}

/* The textarea remains the sole input/selection surface. A pointer-inert,
   aria-hidden mirror underneath it paints DraftFormat ranges as React spans. */
.alg-composer-input {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}
.alg-composer-preview {
  position: absolute;
  z-index: 0;
  inset: 0;
  padding: 8px 6px;
  overflow: hidden;
  box-sizing: border-box;
  pointer-events: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font: inherit;
  /* КЕГЛЬ ТЕЛА СООБЩЕНИЯ, не font.body: пишешь тем же, чем читаешь.
     После подъёма ленты 15 -> 17 (25.09, кегль iMessage) поле ввода
     на 15 рисовало бы текст мельче, чем он выглядит после отправки,
     и превью разметки расходилось бы с результатом. На iOS композер
     и так .body (ComposerView.swift:691). Мобильный 16px !important
     в mobileCss ОСТАЁТСЯ — это анти-зум WebView, а не типографика. */
  font-size: ${font.messageBody}px;
  line-height: 1.4;
  color: ${color.text};
}
.alg-composer-preview.composing { visibility: hidden; }
.alg-composer-input.disabled .alg-composer-preview { opacity: 0.5; }
.alg-composer-preview-sentinel { font-weight: normal; text-decoration: none; }
.alg-preview-bold { font-weight: 700; }
.alg-preview-italic { font-style: italic; }
.alg-preview-underline { text-decoration: underline; }
.alg-preview-strikethrough { text-decoration: line-through; }
.alg-preview-underline.alg-preview-strikethrough { text-decoration: underline line-through; }
.alg-preview-color-red { color: ${color.composerRed}; }
.alg-preview-color-orange { color: ${color.composerOrange}; }
.alg-preview-color-green { color: ${color.composerGreen}; }
.alg-preview-color-blue { color: ${color.composerBlue}; }
.alg-preview-color-purple { color: ${color.composerPurple}; }

/* ПОЛЕ ВВОДА внутри строки: без рамки и фона — их даёт контейнер. */
.alg-composer-textarea {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  resize: none;
  font: inherit;
  /* ОБЯЗАН СОВПАДАТЬ С .alg-composer-preview ДО ПИКСЕЛЯ: превью
     лежит РОВНО ПОД прозрачным textarea (inset: 0, тот же padding),
     и расхождение кегля сразу уводит каретку от букв. Поднят до 17
     тем же ходом, что и превью. */
  font-size: ${font.messageBody}px;
  padding: 8px 6px;
  border: none;
  outline: none;
  background: transparent;
  color: transparent;
  -webkit-text-fill-color: transparent;
  caret-color: ${color.text};
  /* Рост ВВЕРХ до ~6 строк, дальше внутренняя прокрутка. */
  max-height: 132px;
  min-height: 32px;
  line-height: 1.4;
  box-sizing: border-box;
}
.alg-composer-textarea::placeholder {
  color: ${color.placeholder};
  -webkit-text-fill-color: ${color.placeholder};
}
.alg-composer-textarea.composing {
  color: ${color.text};
  -webkit-text-fill-color: ${color.text};
}
.alg-composer-textarea:focus {
  outline: none;
  border-color: ${color.accent};
}
.alg-composer-textarea:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Voice recorder replaces the normal input row; no auto-send on stop. */
.alg-composer-voice {
  margin: 8px 14px 12px;
  padding: 3px;
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${color.border};
  border-radius: ${radius.pill}px;
  background: ${color.bg};
}
.alg-voice-track {
  min-width: 0;
  flex: 1 1 auto;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.alg-voice-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${color.textDim};
}
.alg-voice-dot.active {
  background: ${color.composerRed};
  animation: alg-voice-pulse 1s steps(2, end) infinite;
}
@keyframes alg-voice-pulse { 50% { opacity: .35; } }
.alg-voice-wave {
  flex: 1 1 auto;
  height: 24px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}
.alg-voice-wave i {
  display: block;
  width: 3px;
  min-height: 3px;
  max-height: 24px;
  flex: 0 0 3px;
  border-radius: 2px;
  background: ${color.accent};
}
.alg-voice-duration {
  flex: 0 0 auto;
  min-width: 34px;
  color: ${color.textDim};
  font-variant-numeric: tabular-nums;
  font-size: ${font.small}px;
}

/* Formatting is intentionally desktop-only (web desktop + Tauri). */
.alg-format-toolbar { display: none; }
@media (min-width: 768px) {
  .alg-format-toolbar {
    display: flex;
    align-items: center;
    gap: 3px;
    margin: 0 16px 4px;
    min-height: 24px;
  }
}
.alg-format-btn, .alg-color-swatch {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: ${radius.sm}px;
  background: transparent;
  color: ${color.textDim};
  font: inherit;
  font-size: ${font.small}px;
  cursor: pointer;
}
.alg-format-btn:hover, .alg-color-swatch:hover { background: ${color.hover}; color: ${color.text}; }
.alg-format-btn.active {
  background: ${color.accentSoft};
  color: ${color.accent};
}
.alg-format-btn.bold { font-weight: 700; }
.alg-format-btn.italic { font-style: italic; }
.alg-format-btn.underline { text-decoration: underline; }
.alg-format-btn.strikethrough { text-decoration: line-through; }
.alg-format-divider { width: 1px; height: 16px; margin: 0 3px; background: ${color.border}; }
.alg-color-swatch { width: 18px; height: 18px; border-radius: 50%; border: 2px solid ${color.bg}; box-shadow: 0 0 0 1px ${color.border}; }
.alg-color-swatch.active { box-shadow: 0 0 0 2px ${color.accent}; }
.alg-color-swatch.red { background: ${color.composerRed}; }
.alg-color-swatch.orange { background: ${color.composerOrange}; }
.alg-color-swatch.green { background: ${color.composerGreen}; }
.alg-color-swatch.blue { background: ${color.composerBlue}; }
.alg-color-swatch.purple { background: ${color.composerPurple}; }

/* МЕНЮ вложений (плюсик): компактное, над кнопкой. */
.alg-attach-menu {
  position: fixed;
  z-index: 70;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  background: ${color.bg};
  border: 1px solid ${color.border};
  border-radius: ${radius.md}px;
  box-shadow: 0 4px 16px ${color.backdrop};
}
.alg-attach-item {
  font: inherit;
  font-size: ${font.small}px;
  text-align: left;
  padding: 7px 12px;
  border: none;
  background: transparent;
  color: ${color.text};
  cursor: pointer;
  border-radius: ${radius.sm}px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}
.alg-attach-item:hover:not(:disabled) {
  background: ${color.hover};
}

/* БАННЕРЫ ответа и редактирования — как в Telegram: полоска акцентом
   слева, имя/подпись, одна строка цитаты, крестик справа. */
.alg-composer-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 8px;
  border-radius: ${radius.sm}px;
  background: ${color.accentSoft};
  border-left: 3px solid ${color.accent};
  font-size: ${font.small}px;
}
.alg-composer-banner.editing {
  background: ${color.warnBg};
  border-left-color: ${color.warn};
}
.alg-composer-banner-body {
  flex: 1 1 auto;
  min-width: 0;
}
.alg-composer-banner-title {
  font-weight: ${font.weightBold};
  color: ${color.accent};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alg-composer-banner.editing .alg-composer-banner-title {
  color: ${color.text};
}
.alg-composer-banner-snippet {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${color.textDim};
}
.alg-composer-banner-close {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${color.textDim};
  cursor: pointer;
  border-radius: ${radius.sm}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.alg-composer-banner-close:hover {
  background: ${color.hover};
  color: ${color.text};
}

/* Pending attachments are part of the composer draft. Nothing is uploaded
   until the ordinary Send action (or Enter) confirms this preview. */
.alg-pending-attachments {
  display: flex;
  gap: 8px;
  margin: 0 14px 8px;
  overflow-x: auto;
}
.alg-pending-attachment {
  flex: 0 1 280px;
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border: 1px solid ${color.border};
  border-radius: ${radius.md}px;
  background: ${color.hover};
}
.alg-pending-attachment-image,
.alg-pending-attachment-file {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: ${radius.sm}px;
}
.alg-pending-attachment-image { object-fit: cover; }
.alg-pending-attachment-file {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${color.textDim};
  background: ${color.bg};
}
.alg-pending-attachment-meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.alg-pending-attachment-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${color.text};
  font-size: ${font.small}px;
}
.alg-pending-attachment-size {
  color: ${color.textDim};
  font-size: ${font.tiny}px;
}
`;
